import React, { Component } from 'react';
import { Container, Row, Col, Card, Form, Table } from 'react-bootstrap';
import { DatePicker } from 'antd';
import moment from 'moment';
import Side from './Sidebar';
import Top from './Topbar';
import C from '../lib/Config';
import swal from 'sweetalert';

class PieChart extends Component {
    calculateSlices = (data) => {
        const total = data.reduce((sum, item) => sum + item.value, 0);
        
        let startAngle = 0;
        return data
            .filter(item => item.value > 0)
            .map(item => {
                const percentage = item.value / total;
                const angle = percentage * 360;
                
                const x1 = Math.cos(Math.PI * startAngle / 180);
                const y1 = Math.sin(Math.PI * startAngle / 180);
                
                startAngle += angle;
                
                const x2 = Math.cos(Math.PI * startAngle / 180);
                const y2 = Math.sin(Math.PI * startAngle / 180);
                
                const largeArcFlag = angle > 180 ? 1 : 0;
                
                const d = `M 0 0 L ${x1} ${y1} A 1 1 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
                
                return {
                    path: d,
                    color: item.color,
                    label: item.label,
                    value: item.value,
                    percentage: (percentage * 100).toFixed(1)
                };
            });
    };

    render() {
        const { data, size = 300 } = this.props;
        const slices = this.calculateSlices(data);

        if (slices.length === 0) {
            return (
                <div style={{ 
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '300px',  // Adjust this value based on your needs
                    width: '100%',
                    padding: '1rem',
                    fontSize: '16px',
                    color: '#666'  // A softer gray color for better readability
                }}>
                    No data available for displaying status value in Pie Chart
                </div>
            );
        }
        
        const centerTransform = `translate(${size/2}, ${size/2}) scale(${size/2.2})`;
        
        return (
            <div>
                <svg width={size} height={size}>
                    <g transform={centerTransform}>
                        {slices.map((slice, index) => (
                            <path
                                key={index}
                                d={slice.path}
                                fill={slice.color}
                            />
                        ))}
                    </g>
                </svg>
            </div>
        );
    }
}

class ChemoPatientDashboard extends Component {
    constructor(props) {
        super(props);
        this.state = {
            dashboardStats: {
                activePatients: 0,
                inactivePatients: 0,
                onHoldPatients: 0,
                completedTreatments: 0,
                defaultedPatients: 0,
                totalProtocols: 0
            },
            loading: false,
            error: null,
            patientData: [],
            userId: localStorage.getItem('userId'),
            selectedDate: null,
            instituteArr: [],
            selectedInstitute: '',
            dataFetched: false
        };
    }

    componentDidMount() {
        this.getAllInstitute();
    }

    getAllInstitute = () => {
        const userId = this.state.userId;
        fetch(`${C.getUrl()}/institute/getAllInstituteByUserId/${userId}`)
            .then(res => res.json())
            .then(res => {
                const arr = res.map(d => ({
                    id: d.instituteId,
                    name: d.instituteName
                }));
                this.setState({ 
                    instituteArr: arr,
                    selectedInstitute: arr.length === 1 ? arr[0].id : ''
                });
            })
            .catch(err => {
                console.error('Error fetching institutes:', err);
                swal({
                    title: "Error!",
                    text: "Error fetching institutes: " + err,
                    icon: "error",
                    buttons: false,
                    timer: 2000,
                });
            });
    }

    handleDateChange = (date) => {
        this.setState({ selectedDate: date }, () => {
            if (this.state.selectedInstitute && date) {
                this.fetchDashboardData();
            }
        });
    };

    fetchDashboardData = async () => {
        try {
            this.setState({ loading: true });
            const formattedDate = this.state.selectedDate ? 
                moment(this.state.selectedDate, ['DD/MM/yyyy', 'YYYY-MM-DD', 'DD-MM-YYYY']).format('DD-MM-YYYY') : '';
            
            const url = `${C.getUrl()}/patientData/allfilters?userId=${this.state.userId}&instituteId=${this.state.selectedInstitute}&specificDate=${formattedDate}`;
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();
            this.processDashboardData(data);
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            this.setState({ error: error.message });
            swal({
                title: "Error!",
                text: "Error fetching dashboard data: " + error.message,
                icon: "error",
                buttons: false,
                timer: 2000,
            });
        } finally {
            this.setState({ loading: false, dataFetched: true });
        }
    };

    processDashboardData = (data) => {
        if (!Array.isArray(data)) {
            console.error('Invalid data format received:', data);
            return;
        }

        const statusCounts = data.reduce((acc, patient) => {
            const status = patient.status || 'Unknown';
            acc[status] = (acc[status] || 0) + 1;
            return acc;
        }, {});

        const processedPatients = data.map(patient => ({
            name: `${patient.firstName || ''} ${patient.lastName || ''}`.trim(),
            protocol: patient.treatmentPlans?.[0]?.protocolName || 'For this Patient protocol Name is not selected.',
            status: patient.status || 'Patient Status value is not selected'
        }));

        this.setState({
            dashboardStats: {
                activePatients: statusCounts['Active'] || 0,
                inactivePatients: statusCounts['Inactive'] || 0,
                onHoldPatients: statusCounts['On Hold'] || 0,
                completedTreatments: statusCounts['Completed'] || 0,
                defaultedPatients: statusCounts['Default'] || 0,
                totalProtocols: new Set(data.map(p => p.treatmentPlans?.[0]?.protocolName).filter(Boolean)).size
            },
            patientData: processedPatients
        });
    };

    render() {
        const { dashboardStats, loading, error, patientData, selectedDate, dataFetched } = this.state;

        // Updated color scheme
        const statusConfig = {
            'Active': '#4CAF50',      // Green
            'Inactive': '#2196F3',    // Blue
            'On Hold': '#FFC107',     // Amber
            'Completed': '#9C27B0',   // Purple
            'Default': '#F44336'      // Red
        };

        // In the render method, modify the chartData creation:
        const totalPatients = dashboardStats.activePatients + 
        dashboardStats.inactivePatients + 
        dashboardStats.onHoldPatients + 
        dashboardStats.completedTreatments + 
        dashboardStats.defaultedPatients;

const chartData = Object.entries(statusConfig)
.map(([status, color]) => {
const value = status === 'Active' ? dashboardStats.activePatients :
        status === 'Inactive' ? dashboardStats.inactivePatients :
        status === 'On Hold' ? dashboardStats.onHoldPatients :
        status === 'Completed' ? dashboardStats.completedTreatments :
        dashboardStats.defaultedPatients;
return {
label: status,
value: value,
color: color,
percentage: totalPatients > 0 ? ((value / totalPatients) * 100).toFixed(1) : '0'
};
});

        return (
            <div style={{ display: 'flex' }}>
                <Side />
                <div style={{ flex: 1, marginLeft: '100px' }}>
                    <Top />
                    <h6 className="text-success" style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '20px', marginLeft: '30px' }}>
                        Patient Status Summary Report
                    </h6>
                    <div style={{ padding: '10px' }}>
                        <Container fluid>
                            <Card>
                                <Card.Header>
                                    <Card.Title>Patient Status Summary Report</Card.Title>
                                    <div style={{ marginTop: '15px' }}>
                                        <Form.Label>Registration Date</Form.Label>
                                        <DatePicker
                                            onChange={this.handleDateChange}
                                            value={selectedDate}
                                        />
                                    </div>
                                </Card.Header>
                                <Card.Body>
                                    {!dataFetched ? (
                                        <div style={{ textAlign: 'center' }}>
                                            Please select a date to view the dashboard data.
                                        </div>
                                    ) : loading ? (
                                        <div style={{ textAlign: 'center' }}>
                                            Loading dashboard data...
                                        </div>
                                    ) : error ? (
                                        <div style={{ textAlign: 'center', color: 'red' }}>
                                            {error}
                                        </div>
                                    ) : (
                                        <>
                                            <Row className="mb-4">
                                                <Col md={6}>
                                                    <PieChart data={chartData} />
                                                </Col>
                                                <Col md={6}>
                                                    <div style={{ marginTop: '50px' }}>
                                                        {chartData.map((item, index) => (
                                                            <div key={index} style={{ 
                                                                marginBottom: '15px',
                                                                fontSize: '16px',
                                                                display: 'flex',
                                                                alignItems: 'center'
                                                            }}>
                                                                <div style={{
                                                                    width: '20px',
                                                                    height: '20px',
                                                                    backgroundColor: item.color,
                                                                    marginRight: '10px',
                                                                    borderRadius: '3px'
                                                                }} />
                                                                <span>
                                                                    {item.label} ({item.value}) - {item.percentage}%
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col>
                                                    <Table responsive hover>
                                                        <thead>
                                                            <tr>
                                                                <th>Patient Name</th>
                                                                <th>Protocol</th>
                                                                <th>Status</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
    {patientData.length === 0 ? (
        <tr>
            <td colSpan="2" style={{ textAlign: 'center' }}>
                No patients found for selected date
            </td>
        </tr>
    ) : (
        patientData.map((patient, index) => (
            <tr key={index}>
                <td>{patient.name}</td>
                <td>{patient.protocol}</td>
                <td>
                    {patient.status === 'Patient Status value is not selected' ? (
                        <span style={{
                            padding: '5px 10px',
                            borderRadius: '4px',
                            backgroundColor: '#f5f5f5',  // Light gray background
                            color: '#000000',  // Black text
                            fontSize: '0.9em'
                        }}>
                            {patient.status}
                        </span>
                    ) : (
                        <span style={{
                            padding: '5px 10px',
                            borderRadius: '4px',
                            backgroundColor: statusConfig[patient.status],
                            color: 'white',
                            fontSize: '0.9em'
                        }}>
                            {patient.status}
                        </span>
                    )}
                </td>
            </tr>
        ))
    )}
</tbody>
                                                    </Table>
                                                </Col>
                                            </Row>
                                        </>
                                    )}
                                </Card.Body>
                            </Card>
                        </Container>
                    </div>
                </div>
            </div>
        );
    }
}

export default ChemoPatientDashboard;